import React, {useMemo} from 'react'
import {useFormContext, Controller} from 'react-hook-form'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  FormControl,
  FormHelperText,
  styled,
  alpha,
  Chip,
  Stack,
} from '@mui/material'
import {CloudUpload, CheckCircle, Error, ArrowForward, ArrowBack} from '@mui/icons-material'
import {useNavigate} from 'react-router-dom'
import {businessTypes} from './kycConfig'
import {LoadingButton} from '@mui/lab'

const ContentCard = styled(Card)(({theme}) => ({
  width: '100%',
  borderRadius: theme.spacing(3),
  overflow: 'hidden',
  background: 'rgba(255,255,255,0.95)',
  backdropFilter: 'blur(20px)',
}))

const HeaderSection = styled(Box)(({theme}) => ({
  background: 'linear-gradient(135deg, #3783e7 0%, #667eea 100%)',
  color: 'white',
  padding: theme.spacing(3, 4),
}))

const DocumentsContainer = styled(Box)(({theme}) => ({
  padding: theme.spacing(4),
}))

const DocumentCard = styled(Card)(({theme, hasError}) => ({
  border: `2px solid ${hasError ? theme.palette.error.main : 'transparent'}`,
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
  transition: 'all 0.3s ease',
  '&:hover': {
    border: `2px solid ${alpha('#3783e7', 0.3)}`,
  },
}))

const UploadArea = styled(Box)(({theme, isDragActive, hasFile}) => ({
  border: `2px dashed ${
    hasFile ? theme.palette.success.main : isDragActive ? '#3783e7' : alpha('#3783e7', 0.3)
  }`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  background: hasFile
    ? alpha(theme.palette.success.main, 0.05)
    : isDragActive
    ? alpha('#3783e7', 0.05)
    : 'transparent',
  '&:hover': {
    background: alpha('#3783e7', 0.05),
    borderColor: '#3783e7',
  },
}))

const RequirementChip = styled(Chip)(({theme}) => ({
  fontSize: '0.75rem',
  height: 24,
}))

const ActionButton = styled(LoadingButton)(({theme}) => ({
  borderRadius: theme.spacing(3),
  padding: theme.spacing(1.5, 4),
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1rem',
  color: '#fff',
  background: 'linear-gradient(135deg, #3783e7 0%, #667eea 100%)',
}))

function Documents() {
  const {
    control,
    watch,
    formState: {errors},
  } = useFormContext()
  const navigate = useNavigate()

  const watchedBusinessType = watch('businessType')

  // Get documents for selected business type
  const documentList = useMemo(() => {
    const selectedBusinessType = businessTypes.find((type) => type.title === watchedBusinessType)
    return selectedBusinessType?.documents || []
  }, [watchedBusinessType])

  const handleFileUpload = (onChange, file) => {
    if (file) {
      onChange(file)
    }
  }

  const onBackHandler = () => {
    navigate('/profile/verfication/business-type')
  }

  const onSubmitHandler = () => {
    // Form submission will be handled by the parent form
    console.log('Documents submitted')
  }

  if (!watchedBusinessType) {
    return (
      <ContentCard>
        <CardContent sx={{textAlign: 'center', py: 8}}>
          <Typography variant='h6' color='text.secondary'>
            Please select a business type first
          </Typography>
          <Button variant='contained' onClick={onBackHandler} sx={{mt: 2}}>
            Go Back to Business Type Selection
          </Button>
        </CardContent>
      </ContentCard>
    )
  }

  return (
    <ContentCard>
      <HeaderSection>
        <Typography variant='body1' fontWeight={600} gutterBottom color='white'>
          Document Upload - {watchedBusinessType}
        </Typography>
        <Typography variant='body2'>
          Please upload the required documents for your business type. All mandatory documents must
          be uploaded to proceed with verification.
        </Typography>
      </HeaderSection>

      <DocumentsContainer>
        <Grid container spacing={3}>
          {documentList.map((doc) => {
            const fieldError = errors[doc.name]

            return (
              <Grid item xs={6} key={doc.name}>
                <DocumentCard hasError={!!fieldError}>
                  <CardContent>
                    <Box sx={{mb: 2}}>
                      <Stack direction='row' spacing={1} alignItems='center' sx={{mb: 1}}>
                        <Typography variant='h6' fontWeight={500}>
                          {doc.label}
                        </Typography>
                        <RequirementChip
                          label={doc.required ? 'Required' : 'Optional'}
                          variant='filled'
                          size='small'
                          color={doc.required ? 'error' : 'primary'}
                        />
                      </Stack>
                      <Typography variant='body2' color='text.secondary'>
                        {doc.description}
                      </Typography>
                    </Box>

                    <Controller
                      name={doc.name}
                      control={control}
                      render={({field: {onChange, value}}) => (
                        <FormControl fullWidth error={!!fieldError}>
                          <UploadArea
                            hasFile={!!value}
                            onClick={() => {
                              const input = document.createElement('input')
                              input.type = 'file'
                              input.accept = '.pdf,.jpg,.jpeg,.png'
                              input.multiple = !!doc.multiple
                              input.onchange = (e) => {
                                const files = e.target.files
                                if (files && files.length > 0) {
                                  handleFileUpload(onChange, doc.multiple ? files : files[0])
                                }
                              }
                              input.click()
                            }}
                          >
                            {value ? (
                              <Box>
                                <CheckCircle sx={{fontSize: 48, color: 'success.main', mb: 1}} />
                                <Typography variant='body1' fontWeight={500} color='success.main'>
                                  File Uploaded Successfully
                                </Typography>
                                <Typography variant='body2' color='text.secondary'>
                                  {value instanceof File
                                    ? value.name
                                    : value instanceof FileList
                                    ? `${value.length} files selected`
                                    : 'File selected'}
                                </Typography>
                              </Box>
                            ) : (
                              <Box>
                                <CloudUpload sx={{fontSize: 48, color: 'text.secondary', mb: 1}} />
                                <Typography variant='body1' fontWeight={500}>
                                  Click to upload {doc.multiple ? 'files' : 'file'}
                                </Typography>
                                <Typography variant='body2' color='text.secondary'>
                                  Supported formats: PDF, JPG, JPEG, PNG
                                </Typography>
                              </Box>
                            )}
                          </UploadArea>

                          {fieldError && (
                            <FormHelperText sx={{mt: 1, display: 'flex', alignItems: 'center'}}>
                              <Error sx={{fontSize: 16, mr: 0.5}} />
                              {fieldError.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </CardContent>
                </DocumentCard>
              </Grid>
            )
          })}
        </Grid>

        <Box sx={{mt: 4, display: 'flex', justifyContent: 'space-between'}}>
          <Button
            variant='outlined'
            onClick={onBackHandler}
            startIcon={<ArrowBack />}
            sx={{borderRadius: 3, px: 4}}
          >
            Back
          </Button>

          <ActionButton type='submit' onClick={onSubmitHandler} endIcon={<ArrowForward />}>
            Submit Documents
          </ActionButton>
        </Box>
      </DocumentsContainer>
    </ContentCard>
  )
}

export default Documents
